import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SharedModule } from '@/shared.module';
import { LanguageService, StaticSiteService } from '@/services';
import type { ListColumnConfig, StaticSite } from '@/models';

/**
 * Lets the user pick a type 'CASE' static site to embed, via the shared `app-list` component
 * (search + client-side pagination) rather than an inline dropdown — the list can get long.
 */
@Component({
  selector: 'app-select-static-site-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './select-static-site-dialog.component.html',
  styleUrl: './select-static-site-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectStaticSiteDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SelectStaticSiteDialogComponent>);
  private readonly staticSiteService = inject(StaticSiteService);
  private readonly languageService = inject(LanguageService);

  protected readonly sites = signal<StaticSite[]>([]);
  protected readonly loading = signal(true);

  protected readonly columns: ListColumnConfig[] = [
    {
      id: 'name',
      name: this.languageService.translateWithCap('general.words.name'),
      width: '70%',
      sort: true,
    },
    {
      id: 'updatedOn',
      name: this.languageService.translateWithCap('management.static-sites.columns.updated-on'),
      width: '30%',
      date: true,
      time: true,
    },
  ];

  constructor() {
    void this.load();
  }

  private async load(): Promise<void> {
    const { data } = await this.staticSiteService.getAll('CASE');
    this.loading.set(false);
    if (data) {
      this.sites.set(data);
    }
  }

  protected select(site: StaticSite): void {
    this.dialogRef.close(site);
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
